<template>
  <a-layout class="layout-container">
    <transition name="header-toast">
      <div v-if="loginNoticeVisible" class="header-login-notice">
        {{ loginNoticeText }}
      </div>
    </transition>
    <a-layout-header :class="['layout-header', transparentHeader && 'header-transparent']">
      <div class="header-left">
        <a-space :size="8" class="brand-block">
          <img class="brand-logo-image" :src="brandLogoUrl" alt="Phenomics Atlas logo">
          <h1 class="brand-wordmark">PHENOMICS<span class="logo-ai">ATLAS</span></h1>
          <span class="brand-tagline"> 跨物种多维度脑疾病动物表型图谱</span>
        </a-space>
      </div>
      <div class="header-center">
        <nav class="header-menu" aria-label="Primary navigation">
          <button
            type="button"
            :class="['header-menu-item', selectedKeys[0] === 'home' && 'is-active']"
            @click="handleMenuClick('home')"
          >
            首页
          </button>
          <button
            type="button"
            :class="['header-menu-item', selectedKeys[0] === 'data' && 'is-active']"
            @click="handleMenuClick('data')"
          >
            表型图谱
          </button>
          <button
            type="button"
            :class="['header-menu-item', selectedKeys[0] === 'analysis' && 'is-active']"
            @click="handleMenuClick('analysis')"
          >
            表型数据库
          </button>
        </nav>
      </div>
      <div class="header-right">
        <div class="user-profile">
          <template v-if="isLoggedIn">
            <a-dropdown
              trigger="click"
              content-class="user-menu-popup"
              @popup-visible-change="(visible) => (userMenuVisible = visible)"
            >
              <div :class="['user-info-trigger', userMenuVisible && 'is-open']">
                <a-avatar :size="28" class="user-avatar">{{ userInfo.avatar }}</a-avatar>
                <span class="username">{{ userInfo.username }}</span>
                <svg class="dropdown-icon" viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M3 5l3 3 3-3" />
                </svg>
              </div>
              <template #content>
                <a-doption @click="handleLogout">退出登录</a-doption>
              </template>
            </a-dropdown>
          </template>
          <template v-else>
            <a-button type="primary" size="small" class="login-btn" @click="handleLogin">
              登录
            </a-button>
          </template>
        </div>
        <div class="language-switcher">
          <span :class="['lang-item', getLocale === 'en' && 'is-active']" @click="changeLocale('en')">EN</span>
          <span class="lang-divider">/</span>
          <span :class="['lang-item', getLocale === 'zh-CN' && 'is-active']" @click="changeLocale('zh-CN')">中文</span>
        </div>
      </div>
    </a-layout-header>
    <a-layout-content class="layout-content">
      <div class="content-inner">
        <!-- <slot name="content"></slot> -->
        <router-view />
      </div>
    </a-layout-content>
    <a-layout-footer v-if="$slots.footer" class="layout-footer">
      <slot name="footer"></slot>
    </a-layout-footer>
  </a-layout>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useLocale } from '@/lang/useLocale';
import brandLogoUrl from '@/assets/images/public/logo1.png';

defineProps({
  transparentHeader: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const route = useRoute();

const isLoggedIn = ref(false);
const userMenuVisible = ref(false);
const userInfo = ref({
  username: 'Lab_Admin_01',
  avatar: 'U'
});

const syncUserLoginState = () => {
  const token = localStorage.getItem('auth_token');
  const rawUserInfo = localStorage.getItem('user_info');

  isLoggedIn.value = Boolean(token);

  if (!rawUserInfo) {
    userInfo.value = {
      username: 'Lab_Admin_01',
      avatar: 'U',
    };
    return;
  }

  try {
    const parsed = JSON.parse(rawUserInfo);
    const username = parsed?.nickname || parsed?.account || '用户';
    userInfo.value = {
      username,
      avatar: String(username).slice(0, 1).toUpperCase(), 
    };
  } catch (error) {
    userInfo.value = {
      username: 'Lab_Admin_01',
      avatar: 'U',
    };
  }
};

onMounted(() => {
  syncUserLoginState();
});

const handleLogin = () => {
  router.push('/login');
};

const handleLogout = () => {
  userMenuVisible.value = false;
  isLoggedIn.value = false;
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_info');
  router.push('/login');
};
const { getLocale, changeLocale: _changeLocale } = useLocale();
const changeLocale = (lang: string) => {
  _changeLocale(lang);
  // Optional: If you want to force a refresh like the original code did
  // setTimeout(() => {
  //   window.location.reload();
  // }, 200);
};

const selectedKeys = ref<string[]>([]);
const authRequiredRoutes = new Set(['data', 'analysis'])
let loginRedirectTimer: number | null = null
let loginNoticeTimer: number | null = null
const loginNoticeVisible = ref(false)
const loginNoticeText = ref('请先登录后再访问该页面')

const scheduleLoginRedirect = (redirectPath: string) => {
  if (loginRedirectTimer) {
    window.clearTimeout(loginRedirectTimer)
  }
  if (loginNoticeTimer) {
    window.clearTimeout(loginNoticeTimer)
  }

  loginNoticeText.value = '请先登录后再访问该页面'
  loginNoticeVisible.value = true
  loginNoticeTimer = window.setTimeout(() => {
    loginNoticeVisible.value = false
    loginNoticeTimer = null
  }, 500)

  loginRedirectTimer = window.setTimeout(() => {
    router.push({
      name: 'login',
      query: {
        redirect: redirectPath,
        // notice: loginNoticeText.value,
      },
    })
    loginRedirectTimer = null
  }, 900)
}

const handleMenuClick = (key: string) => {
  if (authRequiredRoutes.has(key) && !isLoggedIn.value) {
    scheduleLoginRedirect(`/${key}`);
    return;
  }
  if (key && route.name !== key) {
    router.push({ name: key });
  }
};

watch(
  () => route.name,
  (newName) => {
    if (newName) {
      selectedKeys.value = [newName as string];
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (route.name) {
    selectedKeys.value = [route.name as string];
  }
});

onUnmounted(() => {
  if (loginRedirectTimer) {
    window.clearTimeout(loginRedirectTimer)
    loginRedirectTimer = null
  }
  if (loginNoticeTimer) {
    window.clearTimeout(loginNoticeTimer)
    loginNoticeTimer = null
  }
});
</script>

<style scoped lang="less">
.layout-container {
  min-height: 100vh;
  background-color: var(--color-bg-page);
  width: 100%;
  display: flex;
  flex-direction: column;
}

.header-login-notice {
  position: fixed;
  top: 22px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1200;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid rgba(62, 207, 142, 0.32);
  background: rgba(15, 15, 15, 0.96);
  color: #fafafa;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(12px);
  white-space: nowrap;
}

.header-toast-enter-active,
.header-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.header-toast-enter-from,
.header-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}

.layout-header {
  height: 70px;
  background: rgba(23, 23, 23, 0.92);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
  border-bottom: 1px solid var(--color-border-subtle);
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
  backdrop-filter: blur(18px);

  &.header-transparent {
    background: rgba(23, 23, 23, 0.78);
    border-bottom-color: transparent;
    position: absolute;
  }
}

.header-left {
  display: flex;
  align-items: center;
  height: 100%;
  flex: 1 1 0;
  min-width: 0;
  .logo-img{
    width: 150px;
    height: 50px;
    flex-shrink: 0;
  }
}

.header-center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  max-width: none;
  white-space: nowrap;
  z-index: 1;
}

.brand-block {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  white-space: nowrap;
}

.brand-wordmark {
  margin: 0;
  color: var(--color-text-primary);
  font-family: var(--font-display);
  font-size: 1.8rem;
  font-weight: 600;
  font-style: normal;
  display: inline-block;
  white-space: nowrap;
  flex-shrink: 0;
  transform: skewX(-10deg);
  transform-origin: left center;
  line-height: 1;
  letter-spacing: -0.06em;
  margin-left: 4px;
}
.brand-tagline{
  margin-left: 4px;
  white-space: nowrap;
  font-size: 1.3rem;
  color: #fff;
  flex-shrink: 0;
  transform: skewX(-10deg);
  transform-origin: left center;
  line-height: 1;
  font-weight: 600;

}
.brand-logo-image {
  height: 38px;
  width: auto;
  flex-shrink: 0;
  display: block;
  object-fit: contain;
}

.logo-ai {
  color: var(--color-brand);
}

.logo-icon {
  width: 24px;
  height: 24px;
  background-color: #006b5e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-radius: 4px;
  font-size: 14px;
  flex-shrink: 0;
}

.logo-text {
  font-size: 18px;
  font-weight: 700;
  color: #1d2129;
  margin-left: 8px;
  flex-shrink: 0;
}

.header-divider {
  width: 1px;
  height: 24px;
  margin: 0 24px;
  background-color: #e5e6eb;
  flex-shrink: 0;
}

.header-menu {
  display: inline-flex;
  align-items: stretch;
  gap: 4px;
  width: max-content;
  min-width: max-content;
  height: 60px;
  margin: 0;
  pointer-events: auto;
}

.header-menu-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 0 20px 6px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 14px;
  font-weight: 500;
  font-family: var(--font-display);
  line-height: 1;
  white-space: nowrap;
  border-radius: 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 18px;
    left: 20px;
    right: 20px;
    height: 2px;
    background: var(--color-brand);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover {
    color: #3ecf8e;
    background: transparent;
  }

  &:hover::after,
  &.is-active::after {
    transform: scaleX(1);
  }

  &.is-active {
    color: #fff;
    font-weight: 600;
  }
}

.external-icon {
  font-size: 12px;
  margin-left: 4px;
  color: #fff;
  vertical-align: middle;
}

.header-right {
  display: flex;
  align-items: center;
  white-space: nowrap;
  justify-content: flex-end;
  flex: 1 1 0;
  min-width: 0;
}
:deep(.arco-link){
  &:hover {
    background: transparent !important;
  }
}
.api-link {
  color: #fff;
  font-size: 14px;
  text-decoration: none;
  
  &:hover {
    // color: #165dff;
    text-decoration: underline;
  }
}


.user-profile {
  display: flex;
  align-items: center;
  // padding: 4px 8px;
  border-radius: var(--radius-sm);
}

.user-info-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;

  &:hover {
    background-color: var(--color-bg-soft);
  }

  &.is-open {
    background-color: var(--color-bg-soft);
    border-color: var(--color-border-accent);

    .dropdown-icon {
      color: var(--color-brand);
      transform: rotate(180deg);
    }
  }
}

.login-btn {
  // min-width: 112px;
  // height: 40px;
  // padding: 0 24px;
  font-weight: 500;
  font-family: var(--font-display);
  border-radius: var(--radius-pill);
  background: transparent !important;
  // border: 1px solid rgba(62, 207, 142, 0.4) !important;
  color: #fff !important;
  position: relative;
  box-shadow: none !important;
  outline: none !important;
  transition: background-color 0.2s, border-color 0.2s, opacity 0.2s;

  &:hover {
    color: #3ecf8e !important;
    // background: transparent !important;
    // border-color: #52d899 !important;
  }

  &:active, &:focus, &:focus-within {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }

  /* 彻底去除 Arco Button 的默认边框和点击波纹效果 */
  :deep(.arco-btn-inner) {
    border: none !important;
  }
}

.user-avatar {
  background-color: var(--color-brand-faint);
  color: var(--color-brand);
  margin-right: 8px;
  font-weight: bold;
  border: 1px solid var(--color-border-accent);
}

.username {
  font-size: 14px;
  color: var(--color-text-primary);
  margin-right: 4px;
}

.dropdown-icon {
  font-size: 12px;
  color: var(--color-text-muted);
  transition: transform 0.2s ease, color 0.2s ease;
}

:deep(.user-menu-popup .arco-dropdown) {
  min-width: 136px;
  padding: 6px;
  border: 1px solid var(--color-border-default);
  border-radius: 14px;
  background: rgba(23, 23, 23, 0.96);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(18px);
}

:deep(.user-menu-popup .arco-dropdown-list-wrapper) {
  max-height: none;
}

:deep(.user-menu-popup .arco-dropdown-option) {
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 500;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

:deep(.user-menu-popup .arco-dropdown-option-content) {
  display: flex;
  align-items: center;
  line-height: 1;
}

:deep(.user-menu-popup .arco-dropdown-option-active),
:deep(.user-menu-popup .arco-dropdown-option:not(.arco-dropdown-option-disabled):hover) {
  color: var(--color-brand);
  background: var(--color-brand-faint);
  border-color: var(--color-border-accent);
}

.language-switcher {
  display: flex;
  align-items: center;
  margin-left: 16px;
  font-size: 14px;
  color: var(--color-text-muted);
}

.lang-item {
  cursor: pointer;
  padding: 0 4px;
  transition: color 0.2s;
  position: relative;
  color: var(--color-text-muted);

  &:hover {
    color: #3ecf8e;
  }

  &.is-active {
    color: #fff;
    font-weight: 500;

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 4px;
      right: 4px;
      height: 1px;
      background-color: var(--color-brand);
    }
  }
}

@media (max-width: 1680px) {
  .layout-header {
    padding: 0 28px;
  }

  .brand-wordmark {
    font-size: 1.55rem;
  }

  .brand-tagline {
    font-size: 1rem;
  }

  .header-menu-item {
    padding: 0 16px 6px;
    font-size: 13px;

    &::after {
      left: 16px;
      right: 16px;
    }
  }
}

@media (max-width: 1440px) {
  .layout-header {
    padding: 0 20px;
  }

  .brand-logo-image {
    height: 34px;
  }

  .brand-wordmark {
    font-size: 1.4rem;
  }

  .brand-tagline {
    font-size: 0.88rem;
  }

  .header-center {
    max-width: calc(100% - 420px);
  }

  .header-menu-item {
    padding: 0 14px 6px;
  }
}

@media (max-width: 1280px) {
  .brand-tagline {
    display: none;
  }

  .header-center {
    max-width: calc(100% - 280px);
  }

  .header-menu-item {
    padding: 0 12px 6px;
    font-size: 12px;

    &::after {
      left: 12px;
      right: 12px;
    }
  }
}

@media (max-width: 1100px) {
  .layout-header {
    height: auto;
    min-height: 70px;
    flex-wrap: wrap;
    row-gap: 10px;
    padding: 12px 16px 10px;
  }

  .header-left,
  .header-right {
    flex: 1 1 auto;
  }

  .header-center {
    position: static;
    order: 3;
    width: 100%;
    max-width: none;
    transform: none;
    justify-content: center;
  }

  .header-menu {
    width: 100%;
    min-width: 0;
    height: auto;
    justify-content: center;
    flex-wrap: wrap;
    gap: 2px 4px;
  }

  .header-menu-item {
    min-height: 40px;
    padding: 0 12px 4px;

    &::after {
      bottom: 8px;
    }
  }
}

@media (max-width: 768px) {
  .layout-header {
    padding: 10px 12px 8px;
  }

  .brand-block {
    gap: 4px;
  }

  .brand-logo-image {
    height: 30px;
  }

  .brand-wordmark {
    font-size: 1.18rem;
    margin-left: 0;
  }

  .header-right {
    gap: 8px;
  }

  .user-info-trigger {
    padding: 2px 4px;
  }

  .username {
    display: none;
  }

  .language-switcher {
    margin-left: 8px;
    font-size: 12px;
  }

  .header-center {
    justify-content: flex-start;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .header-center::-webkit-scrollbar {
    display: none;
  }

  .header-menu {
    width: max-content;
    min-width: 100%;
    justify-content: flex-start;
    flex-wrap: nowrap;
  }
}

.lang-divider {
  margin: 0 2px;
  color: var(--color-border-strong);
}

.layout-content {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.content-inner {
  // padding: 24px;
  box-sizing: border-box;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.layout-footer {
  padding: 24px;
  background: var(--color-bg-page);
  border-top: 1px solid var(--color-border-subtle);
  width: 100%;
  box-sizing: border-box;
}
</style>
